import zoomSdk, { RunningContext } from '@zoom/appssdk';
import { useCallback, useEffect, useState } from 'react';
import { useMeetingStatus } from 'utils/contexts/meeting-status-context';
import axios from 'utils/services/axios';
import configureApp from 'utils/services/zoom';
import { Meeting } from 'utils/types';
import customAxios from 'axios';

const useZoom = () => {
  const [meeting, setMeeting] = useState<Meeting.IMeeting>({
    joinUrl: '',
    userId: '',
    isHost: false,
  });
  const [isInMeeting, setIsInMeeting] = useState<boolean>(false);
  const [runningContext, setRunningContext] = useState<RunningContext | null>(
    null
  );
  const [userContextStatus, setUserContextStatus] = useState<
    'authorized' | 'unauthorized' | null
  >(null);

  const [isAuthorized, setIsAuthorized] = useState(false);

  const [roleError, setRoleError] = useState<boolean>(false);

  const [initialLoad, setInitialLoad] = useState(true);

  const [error, setError] = useState<boolean>();

  const { setIsGettingMeetingInfo } = useMeetingStatus();

  const configure = useCallback(async () => {
    setInitialLoad(true);
    const configResponse = await configureApp();
    setRunningContext(configResponse.runningContext);
    setUserContextStatus(configResponse.auth.status);
    checkIsUserAuthorized(configResponse.auth.status);
    zoomSdk.onAuthorized(handleOnAuthorized);
    zoomSdk.onMyUserContextChange(handleContextChange);
  }, []);

  const authorize = async () => {
    let authorizeResponse;
    try {
      authorizeResponse = (await axios.get('/zoomapp/authorize')).data;
      if (!authorizeResponse || !authorizeResponse.codeChallenge) {
        console.error(
          'Error in the authorize flow - likely an outdated user session.  Please refresh the app'
        );
        return;
      }
    } catch (e) {
      console.error(e);
    }
    const { codeChallenge, state } = authorizeResponse;

    const authorizeOptions = {
      codeChallenge,
      state,
    };

    try {
      await zoomSdk.authorize(authorizeOptions);
    } catch (e) {
      console.error(e);
    }
  };

  const getMeetingInformation = async () => {
    console.log(' Getting meeting information');
    const {
      participantUUID: userUuid,
      role,
      participantId: userId, // Participant Id is deprecated but the TerpNet requires it. We'll need to change this in the future
    } = await zoomSdk.getUserContext();
    try {
      const { joinUrl } = await zoomSdk.getMeetingJoinUrl();
      const meetingID = (await zoomSdk.getMeetingContext()).meetingID;
      const passcodeResponse = await axios.get(
        `meeting?meetingId=${meetingID}`
      );
      setIsGettingMeetingInfo(false);
      setMeeting({
        joinUrl,
        userId,
        userUuid,
        isHost: role === 'host',
        password: passcodeResponse.data as string,
      });
    } catch (error) {
      if (customAxios.isAxiosError(error)) {
        const response = error.response;
        if (response && response.status !== 404) {
          setRoleError(true);
        }
      }
      setIsGettingMeetingInfo(false);
      setError(true);
      setMeeting({
        joinUrl: '',
        userId,
        userUuid,
        isHost: role === 'host',
        password: undefined,
      });
    }
  };

  const handleOnAuthorized = async (event: any) => {
    const { code, state, redirectUri } = event;

    try {
      const result = (
        await axios.post(
          '/zoomapp/onauthorized',
          {
            code,
            state,
            redirectUri,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        )
      ).data;
      if (result) {
        setIsAuthorized(!!result);
      }
    } catch (e: any) {
      console.error(e.message);
      setError(true);
    }
  };

  const handleContextChange = async (event: any) => {
    await getMeetingInformation();
    setUserContextStatus(event.status);
  };

  const checkIsUserAuthorized = useCallback(async (status: string | null) => {
    if (status === 'authorized') {
      try {
        const isUserAuthorized = (await axios.get('/zoomapp/isauthorized'))
          .data;
        if (isUserAuthorized) {
          setIsAuthorized(true);
        } else {
          console.log('User needs to authorize');
          await authorize();
          setIsAuthorized(true);
        }
      } catch (e: any) {
        console.error(e.message);
        setError(true);
        setIsAuthorized(false);
      } finally {
        setInitialLoad(false);
      }
    } else {
      setIsAuthorized(false);
    }
  }, []);

  useEffect(() => {
    configure();
  }, [configure]);

  useEffect(() => {
    if (runningContext === 'inMeeting' && isAuthorized) {
      setIsInMeeting(true);
      getMeetingInformation();
      if (roleError) {
        setIsGettingMeetingInfo(false);
      }
    } else {
      setIsInMeeting(false);
    }
  }, [runningContext, isAuthorized]);

  return {
    meeting,
    isInMeeting,
    initialLoad,
    isAuthorized,
    error,
    roleError,
  };
};

export default useZoom;
